import { Component, ElementRef, OnInit, ViewChild, HostListener, AfterViewInit } from '@angular/core';
import * as moment from 'moment';
import { DomSanitizer } from '@angular/platform-browser';
import { SMEChatService } from '../../smeChat.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import FarmSiteModel from 'src/app/services/farmSite.model';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import { PondSectionService } from 'src/app/services/pondSection.service';
import PondSectionModel from '../../services/pondSection.model';
import { PersonsService } from '../../services/persons.service';
import PersonsModel from 'src/app/services/persons.model';
import { ReadingsService } from '../../services/readings.service';
import ReadingsModel from '../../services/readings.model';
import { SamplingService } from '../../services/sampling.service';
import SamplingModel from 'src/app/services/sampling.model';
import { SessionService } from '../../services/session.service';
import SessionModel from 'src/app/services/session.model';
import { FarmTeleHealthService } from '../../services/farmTeleHealth.service';
import FarmTeleHealthModel from '../../services/farmTeleHealth.model';
import CustomerPondModel from '../../services/customerPond.model';
import { CustomerPondService } from '../../services/customerPond.service';
import { ImageDetailsService } from '../../services/images-details.service';
import ImageDetailsModel from 'src/app/services/images-details.model';
import { ClimateParamsService } from '../../services/climate-params.service';
import ClimateParamsModel from '../../services/climate-params.model';
import { FeedLogService } from '../../services/feedLog.service';
import FeedLogModel from '../../services/feedLog.model';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { BusinessEntityService } from 'src/app/services/businessEntity.service';


declare var $: any;

/**
 * @component SMEDashboardComponent
 * @selector app-sme-dashboard
 * @implements {OnInit, AfterViewInit}
 *
 * @description
 * The `SMEDashboardComponent` provides a comprehensive dashboard for Subject Matter Experts (SMEs)
 * to monitor farm sites, ponds, and communicate with farm operators.
 *
 * ## Key Responsibilities:
 * 1. **Farm Monitoring**: Displays assigned farm sites and their associated ponds.
 * 2. **Pond Analytics**: Shows detailed metrics for selected ponds including:
 *    - Water Quality Readings (Temperature, DO, pH, etc.)
 *    - Sampling Data (ABW, Biomass)
 *    - Feed Logs
 *    - Climate Parameters
 *    - Image Gallery (Pond/Culture images)
 * 3. **Communication (TeleHealth)**: Integrated chat system allowing SMEs to communicate with farm staff.
 *    - Real-time messaging via WebSockets (`SMEChatService`).
 *    - File/Image sharing in chat.
 *    - Message history and unread count tracking.
 * 4. **Data Visualization**: Carousel view for active ponds and detailed modals for historical data.
 * 5. **File Management**: Uploading images/videos related to pond metrics.
 *
 * ## User Roles:
 * - **SME (Subject Matter Expert)**: Primary user, monitors farms and advises.
 * - **Buyer**: Can also use this dashboard if configured (`buyerUserConfig`).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `personID`: Logged-in user ID.
 * - `LoginUserName`: Display name.
 *
 * ## Data Flow:
 * 1. **Initialization**: Loads user context, fetches assigned farm sites (`getLocationsByLoginSme`).
 * 2. **Site Selection**: Loads ponds for the default or selected site (`getPondsByFarmsiteID`).
 * 3. **Chat Initialization**: Connects to socket, fetches chat history (`getChatHistory`), and listens for messages.
 * 4. **Pond Selection**: Fetches specific pond details (`getImages`, `getPondCondition`, `getWaterDetails`).
 *
 * @service FeedLogService - For feed data.
 * @service ClimateParamsService - For climate data.
 * @service ImageDetailsService - For image handling.
 * @service FarmTeleHealthService - For chat history and unread counts.
 * @service SamplingService - For sampling data.
 * @service SessionService - For session management.
 * @service PersonsService - For user details.
 * @service ReadingsService - For water quality readings.
 * @service PondSectionService - For pond hierarchy.
 * @service CustomerPondService - For pond details.
 * @service FarmSiteService - For farm site details.
 * @service SMEChatService - For real-time socket communication.
 * @service BusinessEntityService - For file uploads/downloads.
 * @service CommonMethodsService - For error handling.
 */
@Component({
  selector: 'app-sme-dashboard',
  templateUrl: './sme-dashboard.component.html',
  styleUrls: ['./sme-dashboard.component.scss']
})
export class SMEDashboardComponent implements OnInit, AfterViewInit {
  /** @type {any} Reference to the scrollable chat container. */
  @ViewChild('scrollBottom') scrollBottom;

  /** @type {Array<FarmSiteModel>} List of farm sites assigned to the SME. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {FarmSiteModel} Currently selected farm site. */
  public farmSite: FarmSiteModel;

  /** @type {PersonsModel} Person model for chat receiver lookup. */
  public person: PersonsModel;

  /** @type {Array<PersonsModel>} List of persons (unused). */
  public persons: Array<PersonsModel>;

  /** @type {Array<ReadingsModel>} List of water quality readings for selected pond. */
  public readings: Array<ReadingsModel>;

  /** @type {Array<ReadingsModel>} Extended list of readings for modal view. */
  public moreReadings: Array<ReadingsModel>;

  /** @type {SamplingModel} Current sampling data. */
  public sampling: SamplingModel;

  /** @type {Array<SamplingModel>} List of sampling data. */
  public samplings: Array<SamplingModel>;

  /** @type {Array<SamplingModel>} Extended list of samplings for modal view. */
  public moreSamplings: Array<SamplingModel>;

  /** @type {SessionModel} Current session model. */
  public session: SessionModel;

  /** @type {Array<SessionModel>} List of sessions (unused). */
  public sessions: Array<SessionModel>;

  /** @type {PondSectionModel} Current pond section. */
  public pondSection: PondSectionModel;

  /** @type {Array<PondSectionModel>} List of pond sections for the site. */
  public pondSections: Array<PondSectionModel>;

  /** @type {Array<FarmTeleHealthModel>} List of chat messages/telehealth records. */
  public farmTeleHealths: Array<FarmTeleHealthModel>;

  /** @type {FarmTeleHealthModel} Current chat message input model. */
  public farmTeleHealth: FarmTeleHealthModel;

  /** @type {Array<FarmTeleHealthModel>} List of chat media attachments. */
  public chatMediaData: Array<FarmTeleHealthModel>;

  /** @type {Array<CustomerPondModel>} List of active ponds for the current site. */
  public activePondsList: Array<CustomerPondModel>;

  /** @type {Array<ImageDetailsModel>} List of images for the selected pond. */
  public imageDetails: Array<ImageDetailsModel>;

  /** @type {ClimateParamsModel} Climate parameters (temperature) for the pond. */
  public climateParam: ClimateParamsModel;

  /** @type {FeedLogModel} Current feed log data. */
  public feedlog: FeedLogModel;

  /** @type {Array<FeedLogModel>} Extended list of feed logs for modal view. */
  public moreFeedlogs: Array<FeedLogModel>;

  /** @type {number} Current page index for ponds carousel. Default 1. */
  public pondsPage: number = 1;

  /** @type {number} Current page index for chat history pagination. Default 0. */
  public page: number = 0;

  /** @type {number} Page number of the currently selected pond. */
  public selectectedPondPage: number;

  /** @type {any} Index of the selected site card. */
  public selectedCardSites: any;

  /** @type {number} Carousel index for pond display. Default 5. */
  public carouselindex: number;

  /** @type {string} Logged-in user role. */
  public loginPersonType: string;

  /** @type {string} Logged-in user ID. */
  public loginPersonID: string;

  /** @type {string} Logged-in user name. */
  public loginUserName: string;

  /** @type {string} Search query string. */
  public search: string;

  /** @type {string} Current temperature display string. */
  public temperature: string;

  // pagination sorting
  /** @type {number} Current page number for lists. Default 1. */
  public currentpagenum: number = 1;

  /** @type {number} Items per page. Default 20. */
  public noofitemsPerPage: number = 20;

  /** @type {string} ID of the currently selected pond. */
  public pondId: string;

  /** @type {string} ID of the current culture in the selected pond. */
  public cultureId: string;

  /** @type {string} Name of the selected business. */
  public selectedBusinessName: string;

  /** @type {CustomerPondModel} Currently selected pond model. */
  public selectedPond: CustomerPondModel;

  /** @type {number} Position index of the selected card. */
  public selectedCardPos: number;

  /** @type {string} Description for file upload. */
  public description: string;

  /** @type {boolean} Flag indicating if a file is selected for upload. */
  public isFileSelected: boolean;

  /** @type {any} Base64 representation of selected file. */
  public selectedbase64file: any;

  /** @type {any} Data payload (unused). */
  public data: any;

  /** @type {string} ID of the current farm site. */
  public siteId: string;

  /** @type {string} ID of the current business. */
  public businessId: string;

  /** @type {string} Type of the file being uploaded. */
  public file_type: string;

  /** @type {string} Base64 string result of file read. */
  public result: string;

  /** @type {string} Name of the selected pond. */
  public pondName: string;

  /** @type {string} ID of the chat receiver (Farm Manager/Operator). */
  public receiverId: string;

  /** @type {Array<any>} List of chat messages. */
  public messagesList = new Array();

  /** @type {string} Name of the selected location/village. */
  public selectedLocationName: string;

  // readingTime: any;
  /** @type {Array<any>} List of records for display (chunked ponds). */
  public recordsList = new Array();

  /** @type {string} Chat user configuration ID. */
  public chatuserConfig: string; // we can remove this

  /** @type {string} Title for toastr (unused). */
  public title = 'angulartoastr';

  /** @type {any} Notification data. */
  public notification: any;

  /** @type {string} Buyer user configuration ID. */
  public buyerUserConfig: string; // we can remove this

  /** @type {any} Selected image object for upload. */
  public imageObject: any;

  /**
   * Constructor for SMEDashboardComponent.
   * Initializes services and models.
   *
   * @param {FeedLogService} feedlogService - Service for feed logs.
   * @param {ClimateParamsService} climateParamsService - Service for climate parameters.
   * @param {ImageDetailsService} imageDetailService - Service for image details.
   * @param {FarmTeleHealthService} farmTeleHealthService - Service for chat/telehealth.
   * @param {SamplingService} samplingService - Service for sampling data.
   * @param {SessionService} sessionService - Service for session management.
   * @param {PersonsService} personService - Service for person details.
   * @param {ReadingsService} readingsService - Service for water readings.
   * @param {PondSectionService} pondSectionService - Service for pond sections.
   * @param {CustomerPondService} customerPondService - Service for customer ponds.
   * @param {FarmSiteService} farmSiteService - Service for farm sites.
   * @param {ElementRef} elementRef - Reference to the component's element.
   * @param {DomSanitizer} sanitizer - Service for DOM sanitization.
   * @param {SMEChatService} socketService - Service for WebSocket chat.
   * @param {Router} router - Angular Router.
   * @param {ToastrService} toastr - Service for toast notifications.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   */
  constructor(public feedlogService: FeedLogService, public climateParamsService: ClimateParamsService, public imageDetailService: ImageDetailsService, public farmTeleHealthService: FarmTeleHealthService, public samplingService: SamplingService, public sessionService: SessionService, public personService: PersonsService, public readingsService: ReadingsService, public pondSectionService: PondSectionService, public customerPondService: CustomerPondService, public farmSiteService: FarmSiteService, public elementRef: ElementRef, public sanitizer: DomSanitizer, public socketService: SMEChatService, public router: Router, public toastr: ToastrService, public sidenavaction: Sidenavaction,
    public commonMethodsService: CommonMethodsService, public businessEntityService: BusinessEntityService) {
    this.farmTeleHealth = new FarmTeleHealthModel();
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Initializes carousel index.
   * - Fetches assigned locations/farm sites.
   * - Initializes socket connection for chat.
   * - Sets up file upload listeners.
   */
  public async ngOnInit() {

    try {
      this.carouselindex = 5;
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.loginPersonID = localStorage.getItem('personID');
      this.loginUserName = localStorage.getItem('LoginUserName');
      $('body').scroll(function (e) {
        e.preventDefault();
      });
      await this.getLocationsByLoginSme();
      this.inItSocketConnection();
      this.addChatAttachment();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @lifecycle ngAfterViewInit
   * @description
   * Lifecycle hook that is called after a component's view has been fully initialized.
   * Scrolls the chat window to the bottom.
   */
  public ngAfterViewInit() {
    if (this.scrollBottom) {
      this.scrollToBottom();
    }
  }

  /**
   * @method scrollToBottom
   * @description Scrolls the chat container to the bottom to show the latest messages.
   */
  public scrollToBottom() {
    if (this.scrollBottom.nativeElement) {
      this.scrollBottom.nativeElement.scrollTop = this.scrollBottom.nativeElement.scrollHeight;
    }
  }

  /**
   * @method getLocationsByLoginSme
   * @async
   * @description Fetches farm sites assigned to the logged-in SME.
   * Initializes the dashboard with the first available site.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async getLocationsByLoginSme() {
    try {
      this.farmSites = await this.farmSiteService.getFarmsitesByPersonID();
      if (this.farmSites.length > 0) {
        this.siteId = this.farmSites[0]['farmSite']['id'];
        this.selectedBusinessName = this.farmSites[0]['farmSite']['businessID']['businessName'];
        this.selectedLocationName = this.farmSites[0]['farmSite']['address']['village'];
        this.chatuserConfig = this.farmSites[0]['farmSite']['chatUserConfig'];
        this.buyerUserConfig = this.farmSites[0]['farmSite']['buyerUserConfig'];
        await this.getPondsByFarmsiteID(this.siteId);
        await this.getMessagesAndUnreadCount();
        await this.getReceiverId();
        await this.siteChange(this.siteId, 0);
      } else {
        alert("No sites are assigned...!");
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getPondsByFarmsiteID
   * @async
   * @description Fetches active ponds for a given farm site ID.
   * Chunks the ponds list for carousel display and selects the first pond.
   * @param {string} locationId - The ID of the farm site.
   */
  public async getPondsByFarmsiteID(locationId: string) {
    try {
      this.recordsList = [];
      this.activePondsList = new Array();
      this.pondSections = await this.pondSectionService.getPondSectionsByFarmSite(locationId);
      if (this.pondSections.length > 0) {
        for (let i = 0; i < this.pondSections.length; i++) {
          let activePonds = this.pondSections[i].Ponds.activePonds;
          activePonds.forEach(pond => {
            this.activePondsList.push(pond);
          })
        }
        let i, j, temparray, chunk = 4;
        for (i = 0, j = this.activePondsList.length; i < j; i += chunk) {
          temparray = this.activePondsList.slice(i, i + chunk);
          this.recordsList.push(temparray);
          // do whatever
        }
        if (this.activePondsList.length > 0) {
          this.pondChange(this.activePondsList[0]['id'], 0);
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getMessagesAndUnreadCount
   * @async
   * @description Fetches unread messages and counts for all assigned farm sites.
   * Updates the `farmSites` array with message data.
   */
  public async getMessagesAndUnreadCount() {
    let personIds = new Array();
    try {
      for (let i = 0; i <= this.farmSites.length - 1; i++) {
        if (this.farmSites[i]['persons'].length > 0) {
          for (let j = 0; j <= this.farmSites[i]['persons'].length - 1; j++) {
            personIds.push({
              'farmSiteID': this.farmSites[i]['farmSite']['id'],
              'id': this.farmSites[i]['persons'][j]['id']
            });
          }
        }
      }
      let req = new FarmTeleHealthModel();
      req.receiverID = this.loginPersonID;
      req.personIDs = personIds;
      this.farmTeleHealths = await this.farmTeleHealthService.getUnreadMessages(req);
      if (this.farmTeleHealths) {
        for (let i = 0; i <= this.farmTeleHealths.length - 1; i++) {
          for (let j = 0; j <= this.farmSites.length - 1; j++) {
            if (this.farmSites[j]['farmSite']['id'] == this.farmTeleHealths[i]['farmSiteID']) {
              this.farmSites[j]['messages'] = this.farmTeleHealths[i]['messages'];
              this.farmSites[j]['unreadCount'] = this.farmTeleHealths[i]['unreadCount'];
              break;
            }
          }
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getReceiverId
   * @async
   * @description Determines the chat receiver ID based on site configuration (chatUserConfig or buyerUserConfig).
   * Fetches chat history for the determined receiver.
   */
  public async getReceiverId() {
    try {
      let userConfig = this.chatuserConfig ? this.chatuserConfig : this.buyerUserConfig;
      this.person = await this.personService.getOneByID(userConfig);
      if (this.person) {
        this.receiverId = this.person['id'];
      }
      await this.getChatHistory(this.receiverId);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getChatHistory
   * @async
   * @description Fetches chat history between the logged-in SME and the receiver.
   * @param {string} receiverId - The ID of the chat receiver.
   */
  public async getChatHistory(receiverId: string) {
    try {
      this.farmTeleHealths = await this.farmTeleHealthService.getMessagesBySenderIdReceiverId(receiverId, this.page);
      const reversedMessage = this.farmTeleHealths.reverse();
      this.messagesList.unshift(...reversedMessage);
      for (let i = 0; i <= this.farmTeleHealths.length - 1; i++) {
        this.farmTeleHealths[i]['date'] = moment(this.farmTeleHealths[i]['createdAt']).format('DD-MMM-YYYY').split(' ')[0] + ' ' + moment(this.farmTeleHealths[i]['createdAt']).format('hh:mm A');
      }
      if (this.page == 0) {
        setTimeout(() => {
          this.scrollToBottom();
        }, 2000)
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method siteChange
   * @async
   * @description Handles the selection of a different farm site.
   * Resets chat, images, and pond data, then loads data for the new site.
   * @param {string} siteId - The ID of the selected site.
   * @param {number} selectedIndex - The index of the selected site card.
   */
  public async siteChange(siteId: string, selectedIndex: number) {
    try {
      let selectedSite;
      this.page = 0;
      this.messagesList = [];
      this.imageDetails = [];
      this.chatMediaData = [];
      this.selectedCardSites = selectedIndex;
      this.pondId = undefined;
      this.cultureId = undefined;
      this.feedlog = undefined;
      this.temperature = 'NA';
      for (let i = 0; i <= this.farmSites.length - 1; i++) {
        if (this.farmSites[i]['farmSite']['id'] == siteId) {
          selectedSite = this.farmSites[i];
          this.farmSites[i].unreadCount = 0;
          break;
        }
      }
      this.siteId = selectedSite['farmSite']['id'];
      this.businessId = selectedSite['farmSite']['businessID']['id'];
      this.selectedBusinessName = selectedSite['farmSite']['businessID']['businessName'];
      this.selectedLocationName = selectedSite['farmSite']['address']['village'];
      this.chatuserConfig = selectedSite['farmSite']['chatUserConfig'];
      this.buyerUserConfig = selectedSite['farmSite']['buyerUserConfig'];
      this.socketService.onReadMessages({
        senderID: this.chatuserConfig ? this.chatuserConfig : this.buyerUserConfig,
        receiverID: this.loginPersonID
      });
      await this.getReceiverId();
      await this.getPondsByFarmsiteID(this.siteId);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method previousPondsPage
   * @description Decrements the pond carousel page index.
   */
  public previousPondsPage() {
    this.pondsPage -= 1;
  }

  /**
   * @method nextPondsPage
   * @description Increments the pond carousel page index.
   */
  public nextPondsPage() {
    this.pondsPage += 1;
  }

  /**
   * @method pondChange
   * @description Handles the selection of a different pond.
   * Loads images and condition data for the selected pond.
   * @param {string} pondId - The ID of the selected pond.
   * @param {number} selectedIndex - The index of the selected pond card.
   */
  public pondChange(pondId: string, selectedIndex: number) {
    try {
      this.selectectedPondPage = this.pondsPage;
      this.selectedCardPos = selectedIndex;
      for (let i = 0; i <= this.activePondsList.length - 1; i++) {
        if (this.activePondsList[i]['id'] == pondId) {
          this.selectedPond = this.activePondsList[i];
          break;
        }
      }
      this.pondId = this.selectedPond['id'];
      if (this.selectedPond['PondCultureData'].length > 0) {
        this.cultureId = this.selectedPond['PondCultureData'][0]['id'];
      }
      this.pondName = this.selectedPond.name;
      this.getImages(this.pondId, this.cultureId);
      this.getPondCondition(this.pondId);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getImages
   * @async
   * @description Fetches images associated with the pond and culture.
   * @param {string} pondId - The pond ID.
   * @param {string} cultureId - The culture ID.
   */
  public async getImages(pondId: string, cultureId: string) {
    try {
      this.imageDetails = [];
      if (pondId != undefined || cultureId != undefined) {
        this.imageDetails = await this.imageDetailService.imageRetrieval(pondId, cultureId);
        if (this.imageDetails.length > 0) {
          for (let i = 0; i < this.imageDetails.length; i++) {
            this.imageDetails[i].fileType = this.checkFileType(this.imageDetails[i].image_url);
            this.businessEntityService.downloadFileFromS3Bucket(this.imageDetails[i].image_url).subscribe(async (response) => {
              if (response) {
                const readFile = new FileReader();
                readFile.readAsDataURL(response);
                readFile.onload = () => {
                  this.imageDetails[i].image_url = readFile.result;
                };
              } else {
                this.imageDetails[i].fileType = 'invalidfile';
              }
            });
            let imageDate = this.imageDetails[i].date;
            if (imageDate) {
              this.imageDetails[i].date = moment(imageDate).format('DD-MMM-YYYY h:mm:ss A');
            }
          }
        }
        await this.getWaterDetails(pondId, cultureId);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getPondCondition
   * @async
   * @description Fetches the current temperature/climate parameters for the pond.
   * @param {string} pondId - The pond ID.
   */
  public async getPondCondition(pondId: string) {
    try {
      const currentDate = moment(new Date()).format('YYYY-MM-DD');
      this.temperature = 'NA';
      this.climateParam = await this.climateParamsService.getClimateParamsByPondIdDate(pondId, currentDate);
      if (this.climateParam['currentDayClimateParams']) {
        if (this.climateParam['currentDayClimateParams'].length > 0) {
          this.temperature = Math.round(this.climateParam['currentDayClimateParams'][0]['temperature']) + ' °C';
        } else {
          this.temperature = 'NA';
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getWaterDetails
   * @async
   * @description Fetches water quality readings for the pond and culture.
   * @param {string} pondId - The pond ID.
   * @param {string} cultureId - The culture ID.
   */
  public async getWaterDetails(pondId: string, cultureId: string) {
    try {
      if (pondId != undefined || cultureId != undefined) {
        this.readings = await this.readingsService.getReadingsByPondIdCultureId(pondId, cultureId);
        for (let i = 0; i < this.readings.length; i++) {
          let ponWaterTime = this.readings[i].time;
          if (ponWaterTime) {
            this.readings[i].time = moment(ponWaterTime).utc().format('DD-MMM-YYYY h:mm A');
          }
        }
        await this.getPondMetricsDetails(pondId, cultureId);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getPondMetricsDetails
   * @async
   * @description Fetches feed log metrics for the pond and culture.
   * @param {string} pondId - The pond ID.
   * @param {string} cultureId - The culture ID.
   */
  public async getPondMetricsDetails(pondId: string, cultureId: string) {
    try {
      if (pondId != undefined || cultureId != undefined) {
        this.feedlog = await this.feedlogService.getPondsByFarmsites(pondId, cultureId);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getViewMoreWaterReading
   * @async
   * @description Fetches all water readings for the pond to display in a modal.
   */
  public async getViewMoreWaterReading() {
    try {
      this.moreReadings = await this.readingsService.getAllReadingsByPondId(this.pondId);
      if (this.moreReadings.length == 0) {
        $('#myModal').modal('hide');
        alert('No data available');
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method viewMoreSampling
   * @async
   * @description Fetches all sampling data for the pond/culture to display in a modal.
   */
  public async viewMoreSampling() {
    try {
      if (this.pondId != undefined || this.cultureId != undefined) {
        this.moreSamplings = await this.samplingService.getSamplingsByPondIdCultureId(this.pondId, this.cultureId);
        if (this.moreSamplings.length == 0) {
          $('#samplingmyModal').modal('hide');
          alert('No data available');
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method viewMoreFeed
   * @async
   * @description Fetches all feed logs for the pond/culture to display in a modal.
   */
  public async viewMoreFeed() {
    try {
      if (this.pondId != undefined || this.cultureId != undefined) {
        this.moreFeedlogs = await this.feedlogService.getFeedlogsByPondIdCultureId(this.pondId, this.cultureId);
        if (this.moreFeedlogs.length == 0) {
          $('#feedmyModal').modal('hide');
          alert('No data available');
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method uploadImage
   * @async
   * @description Uploads a selected image/video file to S3 and saves metadata.
   */
  public async uploadImage() {
    try {
      if (this.data == undefined || this.data == null || this.data == '') {
        alert('Please select file');
        return;
      }
      let imageDetails = new ImageDetailsModel();
      imageDetails.farm_id = this.siteId + '';
      imageDetails.site_id = this.businessId + '';
      imageDetails.section_id = this.selectedPond['section_id'] + '';
      imageDetails.pond_id = this.pondId + '';
      imageDetails.function = 'pond';
      imageDetails.desc = this.description;
      imageDetails.pond_culture_id = this.cultureId + '';
      imageDetails.location = 'Hyderabad';
      imageDetails.fileType = this.file_type;
      if (this.imageObject) {
        this.businessEntityService.uploadFileToS3Bucket(this.imageObject, this.selectedBusinessName, 'pondMetrics', `${this.imageObject.name}`).subscribe(async (response) => {
          if (response) {
            const $filePath = Object.values(response)[0];
            if ($filePath) {
              try {
                imageDetails.image_path = $filePath;
                imageDetails.final_size = this.imageObject.size;
                await this.imageDetailService.imageUpload(imageDetails);
                alert('File uploaded successfully');
                $('#pondfileUpload').modal('hide');
                this.file_type = '';
                this.result = '';
                this.description = '';
                this.getImages(this.pondId, this.cultureId);
              } catch (error) {
                alert(error);
              }
            }
          } else {
            alert('Image Upload failed to s3 bucket');
          }
        });
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method clear
   * @description Clears the file upload form fields.
   */
  public clear() {
    this.file_type = '';
    this.result = '';
    this.data = undefined;
    this.description = '';
  }

  /**
   * @method fileupload
   * @description Handles file selection from input.
   * @param {string} id - The ID of the file input element.
   * @param {boolean} val - Flag indicating if this is a chat attachment.
   */
  public fileupload(id, val) {
    if (val) {
      this.addChatAttachment();
    }
    let filesListID = (<HTMLInputElement>document.getElementById(id));
    let filesList = filesListID.files;
    for (let i = 0; i < filesList.length; i++) {
      this.getBase64(filesList[i], val);
    }
    this.isFileSelected = true;
  }

  /**
   * @method getBase64
   * @description Reads a file and converts it to Base64 string.
   * @param {File} file - The file to read.
   * @param {boolean} val - Flag indicating if this is a chat attachment.
   */
  public getBase64(file, val) {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      let filetype = file.name;
      let filetypearr = filetype.split('.');
      this.file_type = this.checkFileType(filetype);
      if (this.file_type == 'invalidfile') {
        alert('Please select png or jpg or mp4 files');
        this.data = undefined;
        return;
      }
      this.imageObject = file;
      let dataresult = '' + reader.result;
      this.result = dataresult.split('base64,')[1];
      if (val) {
        this.onSendAttachment(this.result, filetypearr[1]);
      }
    };
    reader.onerror = function () {
    };
  }

  /**
   * @method setBase64File
   * @description Sets the selected base64 file string.
   * @param {any} base64String - The base64 string.
   */
  public setBase64File(base64String: any) {
    this.selectedbase64file = base64String;
  };

  /**
   * @method remove
   * @description Removes the selected file.
   */
  public remove() {
    let deleteimage = confirm('Are you sure you want to delete these image?');
    if (deleteimage) {
      this.isFileSelected = false;
      this.setBase64File(null);
    }

  }

  /**
   * @method searchBusiness
   * @description Filters the business list based on search input (jQuery based).
   */
  public searchBusiness() {
    let value = this.search.toLowerCase();
    $('.searchfun').filter(function () {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }

  /**
   * @method checkFileType
   * @description Determines if a file is an image or video based on extension.
   * @param {string | ArrayBuffer} file_path - The file path or name.
   * @returns {string} 'image', 'video', or 'invalidfile'.
   */
  public checkFileType(file_path: string | ArrayBuffer) {
    return (file_path.toString().includes('.jpg') || file_path.toString().includes('.jpeg') || file_path.toString().includes('.png')) ? 'image' : ((file_path.toString().includes('.mp4')) ? 'video' : 'invalidfile');
  };
  
  /**
   * @method transform
   * @description Sanitizes a base64 image string for safe use in the DOM.
   * @param {any} base64Image - The base64 image string.
   * @returns {SafeResourceUrl} The sanitized resource URL.
   */
  public transform(base64Image: any) {
    return this.sanitizer.bypassSecurityTrustResourceUrl('data:image/png;base64, ' + base64Image);
  }

  /**
   * @method getChatMedia
   * @async
   * @description Fetches all media attachments exchanged in chat with the receiver.
   */
  public async getChatMedia() {
    try {
      this.chatMediaData = [];
      //  to get attachments received by login person we are exchanged ids
      this.chatMediaData = await this.farmTeleHealthService.getAttachmentsBySenderIdReceiverId(this.receiverId);
      if (this.chatMediaData.length == 0) {
        $('#chatModal').modal('hide');
        alert('Chat attachments are not available');
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method inItSocketConnection
   * @description Initializes the WebSocket connection for chat and sets up listeners.
   */
  public inItSocketConnection() {
    this.socketService.connect();
    this.socketService.onConnected().subscribe(() => { 
      this.socketService.login({ personID: this.loginPersonID })
    })
    this.socketService.getMessage().subscribe(message => {
      if (this.receiverId == message.senderID) {
        message['date'] = message['date'].split(' ')[0] + ' ' + this.add_5_30_hrs(message['date'].split(' ')[1]);
        this.messagesList.push(message);
        if (this.page == 0) {
          setTimeout(() => {
            this.scrollToBottom();
          }, 100)
        }
      } else {
        for (let i = 0; i < this.farmSites.length; i++) {
          if ((this.farmSites[i]['farmSite']['chatUserConfig'] == message.senderID) || this.farmSites[i]['farmSite']['buyerUserConfig'] == message.senderID) {
            let unreadCount = this.farmSites[i]['unreadCount'] ? this.farmSites[i]['unreadCount'] : 0;
            this.farmSites[i]['unreadCount'] = unreadCount + 1;
          }
        }
        this.toastr.info(message['message']['data'], message['senderName'], { timeOut: 5000 });
        this.notification = message['message']['data'];
      }
    });
    this.socketService.onGetAttachment().subscribe(message => {
      message['date'] = message['date'].split(' ')[0] + ' ' + this.add_5_30_hrs(message['date'].split(' ')[1]);
      this.messagesList.push(message);
      if (this.page == 0) {
        setTimeout(() => {
          this.scrollToBottom();
        }, 100)
      }
    });
  }

  /**
   * @method add_5_30_hrs
   * @description Adds 5 hours and 30 minutes to a time string (IST conversion).
   * @param {any} time - The time string.
   * @returns {string} Formatted time.
   */
  public add_5_30_hrs(time: any) {
    return moment(time, 'HH:mm').add(330, 'minutes').format('hh:mm A');
  };

  /**
   * @method onSendMessage
   * @description Sends a text message via WebSocket.
   */
  public onSendMessage() {
    if (!this.farmTeleHealth.enterdata) {
      return;
    }
    const timeStamp = moment(new Date()).format('MM/DD/YYYY, hh:mm A');
    const msgObj = {
      date: timeStamp,
      //isRead: false,
      message: {
        data: this.farmTeleHealth.enterdata,
        'MIMEType': '.txt'
      },
      receiverID: this.receiverId,
      senderID: this.loginPersonID,
      senderName: this.loginUserName
    };
    this.socketService.sendMessage(msgObj);
    msgObj['date'] = timeStamp;
    this.messagesList.push(msgObj);
    this.farmTeleHealth.enterdata = null;
    if (this.page == 0) {
      setTimeout(() => {
        this.scrollToBottom();
      }, 100)
    }
  };

  /**
   * @method onSendAttachment
   * @description Sends a file attachment via WebSocket.
   * @param {any} base64 - The base64 string of the file.
   * @param {any} fileType - The file extension.
   */
  public onSendAttachment(base64: any, fileType: any) {
    const timeStamp = moment(new Date()).format('MM/DD/YYYY, hh:mm A');
    let msgObj = {
      date: timeStamp,
      //isRead: false,
      message: {
        data: base64,
        'MIMEType': '.' + fileType,
      },
      receiverID: this.receiverId,
      senderID: this.loginPersonID,
      senderName: this.loginUserName
    };
    this.socketService.onSendAttachment(msgObj);
    msgObj['date'] = timeStamp;
    this.messagesList.push(msgObj);
    if (this.page == 0) {
      setTimeout(() => {
        this.scrollToBottom();
      }, 100)
    }
    // this.messageSentEnter();
  };

  /**
   * @method logout
   * @async
   * @description Logs out the current user, clears session, and disconnects socket.
   */
  public async logout() {
    try {
      let userId = localStorage.userID;
      let logout = confirm('Are you sure to logout?');
      if (logout) {
        // AppComponent.isLogin(false);
        this.session = await this.sessionService.sessionLogout(userId);
        localStorage.clear();
        this.onDisconnect();
        this.router.navigate(['/login']);
      }
    } catch (error) {
      alert(this.session.message);
    }
  }

  /**
   * @method onDisconnect
   * @description Disconnects the WebSocket connection.
   */
  public onDisconnect() {
    this.socketService?.disconnect();
  };

  /**
   * @method addChatAttachment
   * @description Sets up jQuery event listeners for the chat attachment button.
   * Triggers the hidden file input.
   */
  public addChatAttachment() {
    $('#btnfile').click(function () {
      $('#chatAttachment').click();
    });

    $('#chatAttachment').change(function () {
      let file = $(this).val().replace(/C:\\fakepath\\/ig, '');

      $('#fname').text(file);
    });
  }

  /**
   * @method messageSentEnter
   * @description Sets up jQuery listener for Enter key on chat input (unused/duplicate).
   */
  public messageSentEnter() {
    document.getElementById('text')
      .addEventListener('keyup', function (e) {
        if (e.keyCode === 13) {
          document.getElementById('submit').click();
        }
      });

    document.getElementById('submit').onclick = function () {
      alert('Clicked!');
    };
  }

  /**
   * @method carouselClick
   * @description Handles navigation clicks for the pond carousel.
   * @param {string} type - 'prev' or 'next'.
   */
  public carouselClick(type: string) {
    if (type == 'prev') {
      if (this.carouselindex == 5) {
        this.carouselindex = this.activePondsList.length - 1;
      } else {
        this.carouselindex = this.carouselindex - 1;
      }
    }
    if (type == 'next') {
      if (this.carouselindex == this.activePondsList.length - 1) {
        this.carouselindex = 5;
      } else {
        this.carouselindex = this.carouselindex + 1;
      }
    }
  }

  /**
   * @method onScroll
   * @description Host listener for scroll events on the chat container.
   * Loads older messages when scrolled to top.
   * @param {any} event - The scroll event.
   */
  @HostListener('scroll', ['$event'])
  public async onScroll(event: any) {
    if (event.target.scrollTop == 0) {
      this.page += 10;
      await this.getChatHistory(this.receiverId);
    }
  }

  /**
   * @method keyDownFunction
   * @description Handles Enter key press to send message.
   * @param {any} event - The keyboard event.
   */
  public keyDownFunction(event) {
    if (event.keyCode === 13) {
      // rest of your code
      this.onSendMessage();
    }
  }
}
